import type { CurrentSubscription, IProfile, Session } from '@setplex/tria-api'
import type { AnalytixUserInfo } from '~/shared/analytix'

import { api } from '@setplex/tria-api'
import { createWatch, merge } from 'effector'
import { v4 as uuidv4 } from 'uuid'
import { runtime } from '~/app/environment'
import { profilesModel as profiles } from '~/entities/profiles'
import { entitySubscriptionsModel as subscription } from '~/entities/subscriptions'
import { SnapshotMap } from '../snapshot-map'

const SNAPSHOTS_TO_KEEP = 5

export type GeneralParameters = AnalytixUserInfo

/**
 * SnapshotMap to store general parameters with their history
 */
export const parameters = new SnapshotMap<GeneralParameters>(
  SNAPSHOTS_TO_KEEP,
  [
    ['deviceOsVersion', runtime.browser.version || '-'], // browser version (from User-Agent string) or "-"
    ['deviceModel', runtime.os.name || 'Unknown'], // operating system name (from User-Agent string) or "Unknown"
    ['deviceName', runtime.browser.name || 'Unknown'], // browser name (from User-Agent string) or "Unknown"
    ['deviceType', 'web'], // always "web"
    // ['deviceMacAddress', undefined], // not available in browser
    // ['deviceSerialNumber', undefined], // not available in browser
    ['networkType', 'Unknown'], // always "Unknown" (no way to know it from the browser)
  ]
)

//
// Update parameters
//

function fromSession(session: Session, forceSessionId: boolean) {
  parameters
    .update('deviceId', session.deviceIdentifier)
    .update('networkIp', session.deviceExternalIP)
    .update('contentProviderId', session.providerId)
    .update('contentUserSubscriberId', session.subscriberId)
    .update('contentUserAccountNumber', session.accountNumber)
    .update('contentUserProfileId', session.profileId?.toString(10))
    .update('organisationId', session.packageId?.toString(10))
    .update('organisationName', session.packageName)
    .update('subscriptionExpirationTime', session.expirationTime)

  if (parameters.get('sessionId') == null || forceSessionId) {
    parameters //
      .update('sessionId', uuidv4())
  }

  parameters //
    .commit()
    .notify()
}

function fromSubscription(subscription: CurrentSubscription | null) {
  parameters
    .update('subscriptionId', subscription?.id.toString(10))
    .update('subscriptionName', subscription?.name ?? undefined)
    .commit()
    .notify()
}

function fromProfile(profile: IProfile | null) {
  parameters
    .update('contentUserProfileId', profile?.id.toString(10))
    .update('contentUserProfileName', profile?.name ?? undefined)
    .commit()
    .notify()
}

function fromReset() {
  parameters
    .snapshot()
    .remove('deviceId')
    .remove('networkIp')
    .remove('contentProviderId')
    .remove('contentUserSubscriberId')
    .remove('contentUserAccountNumber')
    .remove('contentUserProfileId')
    .remove('contentUserProfileName')
    .remove('organisationId')
    .remove('organisationName')
    .remove('subscriptionId')
    .remove('subscriptionName')
    .remove('subscriptionExpirationTime')
    .remove('sessionId')
    .notify()
}

//
// Updates listeners
//

// on /account successful response
createWatch({
  unit: api.session.getAccountFx.doneData,
  fn(session) {
    fromSession(session, false)
  },
})

// on /auth and /auth/guest successful response
createWatch({
  unit: merge([
    api.session.signInFx.doneData, //
    api.session.guestInFx.doneData,
  ]),
  fn(result: { payload: Session }) {
    if (result?.payload) {
      fromSession(result.payload, true)
    } else {
      fromReset()
    }
  },
})

// on /logout
createWatch({
  unit: merge([
    api.session.signOutFx.finally,
    api.events.http.unauthorized, // after any session invalidation
  ]),
  fn() {
    fromReset()
  },
})

// on /v3/subscriptions/current successful response
// on any active subscription change (e.g. after renewal)
createWatch({
  unit: merge([
    api.subscriptions.getCurrentSubscriptionFx.doneData,
    subscription.$currentSubscription.updates,
  ]),
  fn(subscription) {
    fromSubscription(subscription)
  },
})

// on any active profile change
createWatch({
  unit: merge([
    api.profiles.getActiveProfileFx.doneData,
    profiles.$activeProfile.updates,
  ]),
  fn(profile) {
    fromProfile(profile)
  },
})
